import { useEffect, useState } from 'react';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { Medal, TrendingUp, Trophy } from 'lucide-react';
import { db } from '../../firebase/config';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { cn } from '../../lib/utils';

const formatTimeAgo = (timestamp) => {
  if (!timestamp) return '';
  const seconds = Math.floor((new Date() - timestamp.toDate()) / 1000);
  const map = [
    [31536000, 'years'],
    [2592000, 'months'],
    [86400, 'days'],
    [3600, 'hours'],
    [60, 'minutes'],
    [1, 'seconds'],
  ];
  for (const [s, label] of map) {
    const v = Math.floor(seconds / s);
    if (v >= 1) return `${v} ${label} ago`;
  }
  return 'just now';
};

const medalClass = {
  1: 'text-gold',
  2: 'text-silver',
  3: 'text-bronze',
};

const Leaderboard = ({ tournamentId }) => {
  const [players, setPlayers] = useState([]);
  const [mapResultCounts, setMapResultCounts] = useState(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let intervalId = null;

    const fetchData = async () => {
      try {
        const q = tournamentId
          ? query(collection(db, 'tournaments', tournamentId, 'players'), orderBy('points', 'desc'))
          : query(collection(db, 'players'), orderBy('points', 'desc'));

        const [playersSnap, mapResultsSnap] = await Promise.all([
          getDocs(q),
          tournamentId
            ? getDocs(collection(db, 'tournaments', tournamentId, 'mapResults'))
            : Promise.resolve(null),
        ]);

        if (cancelled) return;

        setPlayers(
          playersSnap.docs.map((d, i) => ({
            id: d.id,
            position: i + 1,
            ...d.data(),
          }))
        );

        if (mapResultsSnap) {
          const counts = new Map();
          mapResultsSnap.docs.forEach((d) => {
            const pid = d.data().playerId;
            if (!pid) return;
            counts.set(pid, (counts.get(pid) || 0) + 1);
          });
          setMapResultCounts(counts);
        }
      } catch {
        // anonymous reads may be restricted
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    if (tournamentId) {
      intervalId = setInterval(fetchData, 30000);
    }

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [tournamentId]);

  if (loading) {
    return (
      <div className="divide-y divide-border/60 rounded-md border border-border/60">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex h-14 animate-pulse items-center gap-4 px-3">
            <div className="h-4 w-10 bg-muted" />
            <div className="h-4 w-40 bg-muted" />
            <div className="ml-auto h-4 w-16 bg-muted" />
          </div>
        ))}
      </div>
    );
  }

  if (players.length === 0) {
    return (
      <div className="border border-dashed border-border/60 px-6 py-12 text-center text-sm text-muted-foreground">
        No players on the board yet.
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="w-20">Pos</TableHead>
          <TableHead>Player</TableHead>
          <TableHead className="hidden md:table-cell">Country</TableHead>
          <TableHead className="hidden w-20 text-center sm:table-cell">Maps</TableHead>
          <TableHead className="w-28 text-right">Points</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {players.map((player) => {
          const mapCount = mapResultCounts.get(player.id) || 0;
          const topThree = player.position <= 3;
          return (
            <TableRow key={player.id} className={cn(topThree && 'bg-foreground/[0.02]')}>
              <TableCell>
                <div
                  className={cn(
                    'inline-flex items-center gap-1.5 font-mono text-sm font-semibold tabular-nums',
                    medalClass[player.position] || 'text-foreground'
                  )}
                >
                  {topThree && <Medal className="h-4 w-4" />}
                  {String(player.position).padStart(2, '0')}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      'relative flex h-9 w-9 shrink-0 overflow-hidden rounded-full bg-muted',
                      topThree && 'ring-2 ring-gold ring-offset-2 ring-offset-background'
                    )}
                  >
                    {player.imageUrl ? (
                      <img src={player.imageUrl} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center text-xs font-medium text-muted-foreground">
                        {(player.name || '?').slice(0, 2).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate font-display text-sm font-medium sm:text-base">
                      {player.name}
                    </div>
                    {player.lastPointsAdded?.timestamp && (
                      <div
                        className="inline-flex items-center gap-1 text-[11px] text-success"
                        title={`Last update: ${formatTimeAgo(player.lastPointsAdded.timestamp)}`}
                      >
                        <TrendingUp className="h-3 w-3" />+{player.lastPointsAdded.amount}
                      </div>
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell className="hidden md:table-cell">
                {player.country ? (
                  <div className="flex items-center gap-2">
                    <img
                      src={`https://flagcdn.com/24x18/${player.country.toLowerCase()}.png`}
                      alt=""
                      className="h-[14px] w-[18px] rounded-[2px] object-cover"
                    />
                    <span className="text-sm text-muted-foreground">
                      {player.countryName || player.country}
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground/60">—</span>
                )}
              </TableCell>
              <TableCell className="hidden text-center sm:table-cell">
                {mapCount > 0 ? (
                  <Badge variant="outline" className="font-mono tabular-nums">
                    <Trophy className="h-3 w-3" />
                    {mapCount}
                  </Badge>
                ) : (
                  <span className="text-xs text-muted-foreground/60">—</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                <span
                  className={cn(
                    'font-display text-base font-semibold tabular-nums',
                    topThree && 'text-heat'
                  )}
                >
                  {(player.points || 0).toLocaleString()}
                </span>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default Leaderboard;
